import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Ban, Pencil, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { OnlineClosureDialog, type OnlineClosureBlockRow, type OnlineClosureEditTarget } from "@/components/OnlineClosureDialog";

/** Fermetures à la réservation EN LIGNE (page Horaires). Réservations existantes et saisie équipe non concernées. */
export function OnlineClosuresCard({ establishmentId }: { establishmentId: string | null | undefined }) {
  const { toast } = useToast();
  const [blocks, setBlocks] = useState<OnlineClosureBlockRow[]>([]);
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OnlineClosureEditTarget | null>(null);
  const today = formatDateLocal(new Date());

  const fetchBlocks = useCallback(async () => {
    if (!establishmentId) return;
    const { data, error } = await supabase
      .from("time_slot_blocks")
      .select("id, block_date, start_time, end_time, note, blocks_overlap")
      .eq("establishment_id", establishmentId)
      .gte("block_date", today)
      .order("block_date")
      .order("start_time");
    if (error) { console.error("Erreur chargement fermetures en ligne:", error); return; }
    setBlocks((data || []) as OnlineClosureBlockRow[]);
  }, [establishmentId, today]);

  useEffect(() => {
    fetchBlocks();
    if (!establishmentId) return;
    const channel = supabase
      .channel(`online_closures_${establishmentId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "time_slot_blocks", filter: `establishment_id=eq.${establishmentId}` }, () => fetchBlocks())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [establishmentId, fetchBlocks]);

  const grouped = useMemo(() => {
    const m = new Map<string, OnlineClosureBlockRow[]>();
    blocks.forEach(b => m.set(b.block_date, [...(m.get(b.block_date) || []), b]));
    return Array.from(m.entries());
  }, [blocks]);

  const openForAdd = () => { setEditTarget(null); setOpen(true); };
  const openForEdit = (rows: OnlineClosureBlockRow[], kind: "from" | "hours") => {
    setEditTarget({ kind, rows });
    setOpen(true);
  };

  const remove = async (ids: string[]) => {
    const { error } = await supabase.from("time_slot_blocks").delete().in("id", ids);
    if (error) { toast({ variant: "destructive", title: "Erreur", description: "Impossible de supprimer." }); return; }
    toast({ title: "Fermeture supprimée" });
    fetchBlocks();
  };

  if (!establishmentId) return null;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5" />
            Fermetures en ligne
          </CardTitle>
          <CardDescription className="mt-1">
            Fermez la réservation en ligne pour une date précise (ex. privatisation dès 13:30). Les réservations existantes ne changent pas, aucun e-mail n'est envoyé, et l'équipe peut toujours saisir une réservation.
          </CardDescription>
        </div>
        <Button size="sm" className="w-full shrink-0 sm:w-auto" onClick={openForAdd}>
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </CardHeader>
      <CardContent>
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune fermeture à venir.</p>
        ) : (
          <ul className="space-y-2">
            {grouped.map(([d, rows]) => {
              const from = rows.filter(r => r.blocks_overlap);
              const single = rows.filter(r => !r.blocks_overlap);
              const notes = Array.from(new Set(rows.map(r => r.note).filter(Boolean)));
              return (
                <li key={d} className="rounded-md border p-3">
                  <p className="text-sm font-medium capitalize">{format(parseISO(d), "EEEE d MMMM yyyy", { locale: fr })}</p>
                  {from.map(r => (
                    <div key={r.id} className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-sm">Fermé dès {r.start_time.substring(0, 5)}</span>
                      <div className="flex shrink-0 items-center">
                        <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={`Modifier la fermeture dès ${r.start_time.substring(0, 5)}`} onClick={() => openForEdit([r], "from")}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={`Supprimer la fermeture dès ${r.start_time.substring(0, 5)}`} onClick={() => remove([r.id])}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {single.length > 0 && (
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-sm">{single.map(r => r.start_time.substring(0, 5)).join(", ")} {single.length > 1 ? "fermées" : "fermée"}</span>
                      <div className="flex shrink-0 items-center">
                        <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Modifier ces heures fermées" onClick={() => openForEdit(single, "hours")}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Supprimer ces heures fermées" onClick={() => remove(single.map(r => r.id))}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {notes.length > 0 && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{notes.join(" · ")}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <OnlineClosureDialog
        establishmentId={establishmentId}
        open={open}
        onOpenChange={setOpen}
        editTarget={editTarget}
        onSaved={fetchBlocks}
      />
    </Card>
  );
}
